// src/pages/SecurityPage.jsx
//
// "Login verification" - the user's own 2FA settings. Reached from the
// FleetHeader menu ("Security") for internal admins and client users, and
// from the post-login TwoFactorPrompt (/security?setup=1).
//
// Layout follows mockup 1a (status) and 1b (authenticator setup) exactly.
// States the mockup doesn't draw reuse 1b's anatomy:
//   - password check before setup -> 1b step title + code-input box + footer
//   - backup codes                 -> 1b key box (+ COPY) + footer
//   - turn off / new backup codes  -> 1b step title + code-input box + footer
//   - errors / notices             -> shared ft-toolbar-error / ft-toolbar-success (fleet-theme.css)
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import FleetHeader from '../components/FleetHeader';
import { apiFetchJSON } from '../api/apiClient';
import '../styles/fleet-theme.css';
import './SecurityPage.css';

const TWO_FA_INTRO =
  'With two-factor authentication, you can confirm your identity with an extra ' +
  'security feature in addition to your username and password.';

function groupKey(secret) {
  return (secret || '').replace(/(.{4})/g, '$1 ').trim();
}

function digitsOnly(v) {
  return v.replace(/\D/g, '').slice(0, 6);
}

// 6-digit code, or (allowBackup) an 8-char backup code like ABCD-EFGH
function codeValue(v, allowBackup) {
  if (!allowBackup) return digitsOnly(v);
  return v.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 9);
}

// "If you lose your phone" - shown on the backup codes screen and on the
// "2FA is on" status screen. Uses the 1b numbered-step anatomy.
function LostPhoneHelp({ codesLeft }) {
  return (
    <>
      <p className="sec-text"><b>If you lose your phone</b></p>
      <div className="sec-step">
        <span className="sec-step-num">1</span>
        <p className="sec-step-text">Log in with your username and password as usual.</p>
      </div>
      <div className="sec-step">
        <span className="sec-step-num">2</span>
        <p className="sec-step-text">
          When asked for the authenticator code, tap <b>Lost your phone? Use a backup code</b> and
          enter one of your saved backup codes. Each code works only once
          {typeof codesLeft === 'number' ? <>. You have <b>{codesLeft}</b> left.</> : '.'}
        </p>
      </div>
      <div className="sec-step">
        <span className="sec-step-num">3</span>
        <p className="sec-step-text">
          Once you're in, come back to this Security page, tap <b>Turn off</b>, then
          tap <b>Enable 2FA</b> to set it up again on your new phone.
        </p>
      </div>
      <p className="sec-text-muted">
        No backup codes left, or can't find them? Ask your administrator to reset your 2FA.
      </p>
    </>
  );
}

function Message({ kind, children }) {
  if (!children) return null;
  const isError = kind === 'error';
  return (
    <div className={isError ? 'ft-toolbar-error' : 'ft-toolbar-success'} role={isError ? 'alert' : 'status'}>
      <i className={`fas fa-${isError ? 'exclamation-triangle' : 'check-circle'} me-2`}></i>
      {children}
    </div>
  );
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function SecurityPage({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const scope = user?.user_type === 'client' ? 'client' : 'admin';
  // Where "I've saved them" goes once setup / new codes are done
  const dashboardPath = scope === 'client' ? '/dashboard' : '/admin/dashboard';

  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState('');
  // 'status' | 'password' | 'setup' | 'backup' | 'disable' | 'regen'
  const [view, setView] = useState('status');
  const [setup, setSetup] = useState(null); // { secret, otpauth_uri, qr_svg_base64 }
  const [backupCodes, setBackupCodes] = useState([]);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    try {
      setStatus(await apiFetchJSON('/api/account/security'));
      setLoadError('');
    } catch (e) {
      setLoadError(e.message || 'Could not load security settings');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const go = (next) => {
    setView(next);
    setCode('');
    setPassword('');
    setError('');
    setCopied('');
    if (next !== 'status') setNotice('');
  };

  // Arrived from the post-login prompt -> straight to the password step
  useEffect(() => {
    if (searchParams.get('setup') === '1' && status) {
      setSearchParams({}, { replace: true });
      if (!status.totp_enabled) go('password');
    }
  }, [searchParams, status, setSearchParams]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await apiFetchJSON('/api/account/2fa/totp/setup', {
        method: 'POST',
        body: JSON.stringify({ password }),
      });
      setSetup(res);
      go('setup');
    } catch (e2) {
      setError(e2.message);
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  const submitSetup = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await apiFetchJSON('/api/account/2fa/totp/enable', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      setSetup(null);
      setBackupCodes(res.backup_codes || []);
      go('backup');
      await load();
    } catch (e2) {
      setError(e2.message);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const submitAction = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (view === 'disable') {
        await apiFetchJSON('/api/account/2fa/totp/disable', {
          method: 'POST', body: JSON.stringify({ code }),
        });
        go('status');
        setNotice('Authenticator app turned off. Login codes will be sent to your email.');
      } else {
        const res = await apiFetchJSON('/api/account/2fa/backup-codes', {
          method: 'POST', body: JSON.stringify({ code }),
        });
        setBackupCodes(res.backup_codes || []);
        go('backup');
      }
      await load();
    } catch (e2) {
      setError(e2.message);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const doCopy = async (what, text) => {
    if (await copyText(text)) setCopied(what);
  };

  // ---------------------------------------------------------------- views

  let body;

  if (loadError) {
    body = (
      <div className="sec-body">
        <Message kind="error">{loadError}</Message>
      </div>
    );
  } else if (!status) {
    body = (
      <div className="sec-body">
        <p className="sec-text-muted">Loading…</p>
      </div>
    );
  } else if (view === 'password') {
    body = (
      <form className="sec-body sec-body-steps" onSubmit={submitPassword}>
        <div className="sec-step-title">Confirm it's you</div>
        <div className="sec-stack-12">
          <p className="sec-step-text">Enter your account password to continue.</p>
          <input
            className="sec-input sec-input-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            disabled={busy}
          />
          <Message kind="error">{error}</Message>
        </div>
        <div className="sec-footer">
          <button type="button" className="sec-btn-secondary" onClick={() => go('status')} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="sec-btn-primary" disabled={busy || !password}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </div>
      </form>
    );
  } else if (view === 'setup' && setup) {
    body = (
      <form className="sec-body sec-body-steps" onSubmit={submitSetup}>
        <div className="sec-step-title">Set up your authenticator app</div>

        <div className="sec-step">
          <span className="sec-step-num">1</span>
          <p className="sec-step-text">
            Install <b>Google Authenticator</b> or <b>Microsoft Authenticator</b> on your phone.
          </p>
        </div>

        <div className="sec-step">
          <span className="sec-step-num">2</span>
          <div className="sec-stack-14">
            <p className="sec-step-text">In the app, tap <b>+</b> and scan this QR code.</p>
            <div className="sec-qr">
              <img alt="Authenticator QR code" src={`data:image/svg+xml;base64,${setup.qr_svg_base64}`} />
            </div>
            <div className="sec-key-wrap">
              <span className="sec-key-label">Can't scan? Enter this key:</span>
              <div className="sec-key-row">
                <span className="sec-key">{groupKey(setup.secret)}</span>
                <button type="button" className="sec-copy" onClick={() => doCopy('key', setup.secret)}>
                  {copied === 'key' ? 'COPIED' : 'COPY'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="sec-step">
          <span className="sec-step-num">3</span>
          <div className="sec-stack-12">
            <p className="sec-step-text">Enter the 6-digit code the app now shows for Twenty4Seven.</p>
            <input
              className="sec-input"
              placeholder="123456"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(digitsOnly(e.target.value))}
              disabled={busy}
            />
            <Message kind="error">{error}</Message>
          </div>
        </div>

        <div className="sec-footer">
          <button type="button" className="sec-btn-secondary" onClick={() => { setSetup(null); go('status'); }} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="sec-btn-primary" disabled={busy || code.length !== 6}>
            {busy ? 'Checking…' : 'Turn on'}
          </button>
        </div>
      </form>
    );
  } else if (view === 'backup') {
    body = (
      <div className="sec-body sec-body-steps">
        <div className="sec-step-title">Save your backup codes</div>
        <p className="sec-text-muted">
          Two-factor authentication is on. Save these codes somewhere safe, like a password
          manager or a screenshot. They won't be shown again.
        </p>
        <div className="sec-backup-grid">
          {backupCodes.map((c) => <span key={c} className="sec-key">{c}</span>)}
        </div>
        <LostPhoneHelp />
        <div className="sec-footer">
          <button type="button" className="sec-btn-secondary" onClick={() => doCopy('codes', backupCodes.join('\n'))}>
            {copied === 'codes' ? 'Copied' : 'Copy codes'}
          </button>
          <button type="button" className="sec-btn-primary" onClick={() => navigate(dashboardPath)}>
            I've saved them
          </button>
        </div>
      </div>
    );
  } else if (view === 'disable' || view === 'regen') {
    const allowBackup = view === 'disable';
    body = (
      <form className="sec-body sec-body-steps" onSubmit={submitAction}>
        <div className="sec-step-title">
          {view === 'disable' ? 'Turn off authenticator app' : 'New backup codes'}
        </div>
        <div className="sec-stack-12">
          <p className="sec-step-text">
            {view === 'disable'
              ? 'Enter a code from your authenticator app, or a backup code. Login codes will go to your email again.'
              : 'Enter a code from your authenticator app. Your old backup codes will stop working.'}
          </p>
          <input
            className={'sec-input' + (allowBackup ? ' sec-input-backup' : '')}
            placeholder="123456"
            maxLength={allowBackup ? 9 : 6}
            inputMode={allowBackup ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoCapitalize="characters"
            value={code}
            onChange={(e) => setCode(codeValue(e.target.value, allowBackup))}
            autoFocus
            disabled={busy}
          />
          <Message kind="error">{error}</Message>
        </div>
        <div className="sec-footer">
          <button type="button" className="sec-btn-secondary" onClick={() => go('status')} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            className="sec-btn-primary"
            disabled={busy || code.replace(/-/g, '').length < 6}
          >
            {busy ? 'Checking…' : view === 'disable' ? 'Turn off' : 'Generate'}
          </button>
        </div>
      </form>
    );
  } else {
    // ---- 1a: status ----
    if (status.totp_enabled) {
      body = (
        <div className="sec-body">
          <div className="sec-status-row">
            <span className="sec-pill">AUTHENTICATOR APP</span>
            <span className="sec-status-label">ACTIVE</span>
          </div>
          <div className="sec-step-title">All set, nothing else to do</div>
          <p className="sec-text">
            Two-factor authentication is on. When you log in with your password, you'll also
            enter the 6-digit code from your authenticator app.
          </p>
          <Message kind="notice">{notice}</Message>

          <LostPhoneHelp codesLeft={status.backup_codes_left} />

          <p className="sec-text"><b>When to use these buttons</b></p>
          <p className="sec-text-muted">
            <b>New backup codes</b>: you've used most of your codes, or can't find the ones you
            saved. You get 10 new codes and the old ones stop working.
          </p>
          <p className="sec-text-muted">
            <b>Turn off</b>: you're moving to a new phone, or you no longer want to use an
            authenticator app. Login codes will be emailed to you instead.
          </p>
          <div className="sec-actions-center">
            <button type="button" className="sec-btn-secondary" onClick={() => go('regen')}>New backup codes</button>
            <button type="button" className="sec-btn-secondary" onClick={() => go('disable')}>Turn off</button>
          </div>
        </div>
      );
    } else {
      body = (
        <div className="sec-body">
          <div className="sec-status-row">
            <span className="sec-pill">EMAIL CODES</span>
            <span className="sec-status-label">ACTIVE</span>
          </div>
          <p className="sec-text">{TWO_FA_INTRO}</p>
          <p className="sec-text">
            When you log in with your password, a code is emailed to <b>{status.email_hint || 'your email'}</b>.
          </p>
          <p className="sec-text-muted">An authenticator app is safer than email codes and works without signal.</p>
          <Message kind="notice">{notice}</Message>
          <div className="sec-actions-center">
            <button type="button" className="sec-btn-primary" onClick={() => go('password')}>Enable 2FA</button>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="sec-page">
      <FleetHeader title="Security" user={user} onLogout={onLogout} scope={scope} />
      <div className="sec-card">
        <div className="sec-card-head">
          <div className="sec-card-title">Login verification</div>
          <div className="sec-card-user">{user?.username}</div>
        </div>
        {body}
      </div>
    </div>
  );
}
